import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

/**
 * Display-only panel for web-search / grounding metadata captured on a chain
 * node result. This data is intentionally kept OUT of the node's output (so it
 * never flows downstream or bloats the next step's context window) and is
 * surfaced here as an expandable section: the full answer we looked at, the
 * source links, and the search queries used.
 *
 * Accepts the `metadata` object stored on a node result, which can be either:
 *   - { grounding: {...} }                 (single AI node)
 *   - { grounding_items: [{ index, ... }] } (mapOverArray node, per item)
 */

interface GroundingPayload {
  type?: string;
  enabled?: boolean;
  web_search_queries?: string[];
  sources?: Array<{ title?: string; uri?: string }>;
  source_count?: number;
  rendered_search_entry?: string;
  answer?: string;
  index?: number;
}

interface NodeMetadata {
  grounding?: GroundingPayload;
  grounding_items?: GroundingPayload[];
}

const GroundingBody: React.FC<{ g: GroundingPayload }> = ({ g }) => (
  <Box sx={{ mb: 1 }}>
    {Array.isArray(g.web_search_queries) && g.web_search_queries.length > 0 && (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
          Search queries
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {g.web_search_queries.map((q, i) => (
            <Chip key={i} label={q} size="small" variant="outlined" />
          ))}
        </Stack>
      </Box>
    )}

    {Array.isArray(g.sources) && g.sources.length > 0 && (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
          Sources ({g.source_count ?? g.sources.length})
        </Typography>
        <Stack component="ol" sx={{ pl: 2, m: 0 }} spacing={0.5}>
          {g.sources.map((s, i) => (
            <li key={i}>
              {s.uri ? (
                <Link href={s.uri} target="_blank" rel="noopener noreferrer" variant="body2">
                  {s.title || s.uri}
                </Link>
              ) : (
                <Typography variant="body2" component="span">
                  {s.title}
                </Typography>
              )}
            </li>
          ))}
        </Stack>
      </Box>
    )}

    {g.answer && (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
          Full grounded answer (not passed downstream)
        </Typography>
        <Box
          sx={{
            maxHeight: 280,
            overflow: 'auto',
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            fontSize: 12,
          }}
        >
          {g.answer}
        </Box>
      </Box>
    )}

    {g.rendered_search_entry && (
      <Box>
        <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
          Google Search suggestions
        </Typography>
        <Box
          sx={{ '& a': { color: 'primary.main' } }}
          dangerouslySetInnerHTML={{ __html: g.rendered_search_entry }}
        />
      </Box>
    )}
  </Box>
);

export const GroundingMetadataPanel: React.FC<{
  metadata?: NodeMetadata | null;
  sx?: object;
}> = ({ metadata, sx }) => {
  if (!metadata) return null;
  const single = metadata.grounding;
  const items = metadata.grounding_items;
  const count = items?.length ?? (single ? 1 : 0);
  if (count === 0) return null;

  return (
    <Accordion sx={{ mt: 1, ...sx }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TravelExploreIcon fontSize="small" color="action" />
          <Typography variant="caption" fontWeight="bold">
            Web Search Details
          </Typography>
          <Chip label={`${count} ${count === 1 ? 'result' : 'items'}`} size="small" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Everything the web-search step looked at. Shown for review only — this is not
          included in the step output, so it isn&apos;t sent to later steps.
        </Typography>
        {single && <GroundingBody g={single} />}
        {items &&
          items.map((g, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Chip
                label={`Item ${typeof g.index === 'number' ? g.index + 1 : i + 1}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mb: 1 }}
              />
              <GroundingBody g={g} />
            </Box>
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default GroundingMetadataPanel;
